import React, { useState, useEffect } from 'react';
import Popup from '../Popup/Popup';
import { getBookingById } from '../../api/bookings';
import { useLoader } from '../../hooks/LoaderContext';

export interface DayInfo {
  Type: string;
  StartTime: string;
  EndTime: string;
  TotalCharge: number;
}

export interface BookingResponse {
  BookingId: number;
  WeekCommencing: string;
  Status: string;
  CandidateId: number;
  CandidateName: string;
  PONumber: string;
  PlacementInfo: string;
  ConsultantEmailAddress: string;
  Monday: DayInfo | null;
  Tuesday: DayInfo | null;
  Wednesday: DayInfo | null;
  Thursday: DayInfo | null;
  Friday: DayInfo | null;
}

interface ConfirmPopupProps {
  isOpen: boolean;
  bookingId: number | null;
  isConfirming: boolean;
  setIsConfirming: (value: boolean) => void;
  onClose: () => void;
  onConfirm: (data: { PONumber: string }) => void;
  onDispute: () => void;
  error: string | null;
}

const ConfirmPopup: React.FC<ConfirmPopupProps> = ({ isOpen, bookingId, isConfirming, setIsConfirming, onClose, onConfirm, onDispute, error }) => {
  const [booking, setBooking] = useState<BookingResponse | null>(null);
  const [poNumber, setPoNumber] = useState('');
  const { setIsLoading } = useLoader();

  useEffect(() => {
    if (isOpen && bookingId) {
      setIsLoading(true);
      getBookingById(bookingId)
        .then(setBooking)
        .finally(() => setIsLoading(false));
    }
  }, [isOpen, bookingId, setIsLoading]);

  useEffect(() => {
    if (!isOpen) {
      setPoNumber('');
    }
  }, [isOpen]);

  useEffect(() => {
    if(booking?.PONumber) {
      setPoNumber(booking.PONumber);
    }
  }, [booking]);

  const handleClose = () => {
    setIsConfirming(false);
    setBooking(null);
    setPoNumber('');
    onClose();
  };

  const handleConfirmClick = () => {
    if (poNumber) {
      localStorage.setItem('poNumber', poNumber);
    }
    setIsConfirming(true);
  };

  const handleDisputeClick = () => {
    if (poNumber) {
      localStorage.setItem('poNumber', poNumber);
    }
    onDispute();
  };

  const handleYesClick = () => {
    onConfirm({ PONumber: poNumber || '' });
  };

  const handleNoClick = () => {
    setIsConfirming(false);
  };

  if (!isOpen || !booking) return null;

  const isBtnDisabled = booking.Status === 'CONFIRMED' || booking.Status === 'DISPUTED';

  return (
    <Popup width="33.8542vw" onClose={handleClose}>
      {!isConfirming ? (
        <>
          <h2 className="popup-title">Timesheet #{booking?.BookingId}</h2>
          <div className="timesheet-table">
            <div className="table-header row">
              <div className="col">Day</div>
              <div className="col">Duration</div>
              <div className="col">Rate</div>
            </div>
            <div className="rows">
              {booking?.Monday && (
                <div className="row">
                  <div className="col">Monday</div>
                  <div className="col">{booking.Monday.Type}</div>
                  <div className="col">£{booking.Monday.TotalCharge}</div>
                </div>
              )}
              {booking?.Tuesday && (
                <div className="row">
                  <div className="col">Tuesday</div>
                  <div className="col">{booking.Tuesday.Type}</div>
                  <div className="col">£{booking.Tuesday.TotalCharge}</div>
                </div>
              )}
              {booking?.Wednesday && (
                <div className="row">
                  <div className="col">Wednesday</div>
                  <div className="col">{booking.Wednesday.Type}</div>
                  <div className="col">£{booking.Wednesday.TotalCharge}</div>
                </div>
              )}
              {booking?.Thursday && (
                <div className="row">
                  <div className="col">Thursday</div>
                  <div className="col">{booking.Thursday.Type}</div>
                  <div className="col">£{booking.Thursday.TotalCharge}</div>
                </div>
              )}
              {booking?.Friday && (
                <div className="row">
                  <div className="col">Friday</div>
                  <div className="col">{booking.Friday.Type}</div>
                  <div className="col">£{booking.Friday.TotalCharge}</div>
                </div>
              )}
            </div>
          </div>
          <div className="po-number">
            <label>
              P/O Number: <input 
                type="text" 
                value={poNumber}
                onChange={(e) => setPoNumber(e.target.value)}
                readOnly={isBtnDisabled}
              />
            </label>
          </div>
          {!isBtnDisabled && (
            <div className="btns">
              <button className="btn btn-confirm" onClick={handleConfirmClick}>Confirm</button>
              <button className="btn btn-dispute" onClick={handleDisputeClick}>Dispute</button>
            </div>
          )}
          {error && <p className="error-message">{error}</p>}
        </>
      ) : (
        <>
          <h2 className="popup-title">Sure you want to <br /> confirm this Time Sheet?</h2>
          <div className="btns">
            <button className="btn btn-confirm" onClick={handleYesClick}>Yes</button>
            <button className="btn btn-dispute" onClick={handleNoClick}>No</button>
          </div>
        </>
      )}
    </Popup>
  );
};

export default ConfirmPopup; 